<template>
  <div class="s-setting-container no-border">
    <div class="main-header">
      <div>Alert Preference</div>
      <SaveCancelSection
        v-if="adminRole === 'admin'"
        :cancel="resetPartnerConfig"
        :save="updatePartnerConfig"
        :disabled="!changeCheck"
      />
    </div>
    <AlertPreference
      :emailList="emailList"
      :alertOptions="alertOptions"
      :alertPreferenceList="alertPreferenceList"
      :spaceTempSetting="spaceTempSetting"
      :addAlert="addAlertPreference"
      :removeAlert="removeAlertPreference"
      :toggleActionModal="toggleActionModal"
      :onChange="handleChange"
      :errorObj="errorObj"
    />
    <OfflineReport
      :subscribe="offlineReport"
      :recipients="offlineReportEmail"
      :email="email"
      :addEmail="addRecipient"
      :removeEmail="removeRecipient"
      :onChange="handleChange"
      :errorObj="errorObj"
    />
    <ProcessOverlay
      :show="updating"
      text="Saving"
    />
  </div>
</template>

<script>
import partnerSettingsMixin from '@shared/mixins/partnerSettingsMixin';

import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import AlertPreference from '@shared/components/partnerSettings/AlertPreference.vue';
import OfflineReport from '@shared/components/partnerSettings/OfflineReport.vue';
import SaveCancelSection from '@shared/components/setting/shared/SaveCancelSection.vue';
import emailValidation from '@shared/lib/errorCatch/emailValidation';

export default {
  name: 'AlertPreferenceSection',
  components: {
    AlertPreference,
    OfflineReport,
    ProcessOverlay,
    SaveCancelSection,
  },
  mixins: [partnerSettingsMixin],
  props: {
    alertOptions: Array,
    partnerSettings: Object,
    update: Function,
  },
  data() {
    const {
      LEAD_EMAIL = [],
      ALERT_EMAIL_PREFERENCE = [],
      spaceTemp,
      MEMBERS_OFFLINE_REPORT = null,
      MEMBERS_OFFLINE_REPORT_EMAIL = [],
    } = this.partnerSettings;
    return {
      emailList: LEAD_EMAIL,
      alertPreferenceList: ALERT_EMAIL_PREFERENCE,
      spaceTempSetting: spaceTemp,
      showModal: false,
      modalView: '',
      updating: false,
      offlineReport: MEMBERS_OFFLINE_REPORT,
      offlineReportEmail: MEMBERS_OFFLINE_REPORT_EMAIL,
      email: '',
      errorObj: {},
    };
  },
  computed: {
    changeCheck() {
      const {
        ALERT_EMAIL_PREFERENCE = [],
        spaceTemp,
        MEMBERS_OFFLINE_REPORT = null,
        MEMBERS_OFFLINE_REPORT_EMAIL = [],
      } = this.partnerSettings;
      let changes = false;
      if (ALERT_EMAIL_PREFERENCE.length !== this.alertPreferenceList.length) {
        changes = true;
      }
      if (
        this.alertPreferenceList.includes('High Space Temp')
        && spaceTemp?.highTemp !== this.spaceTempSetting?.highTemp
      ) {
        changes = true;
      }
      if (
        this.alertPreferenceList.includes('Low Space Temp')
        && spaceTemp?.lowTemp !== this.spaceTempSetting?.lowTemp
      ) {
        changes = true;
      }
      if (MEMBERS_OFFLINE_REPORT !== this.offlineReport) {
        changes = true;
      }
      if (MEMBERS_OFFLINE_REPORT_EMAIL.length !== this.offlineReportEmail.length) {
        changes = true;
      }
      if (!changes) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
      } else if (changes && !this.showWarningOnExit && !this.warningTab) {
        this.changePartnerSettingsValue('showWarningOnExit', true);
      }
      return changes;
    },
  },
  methods: {
    addRecipient() {
      if (emailValidation(this.email)
        && !this.offlineReportEmail.includes(this.email)) {
        const newEmails = this.offlineReportEmail.slice();
        newEmails.push(this.email);
        this.offlineReportEmail = newEmails;
        this.email = '';
      }
    },
    removeRecipient(email) {
      const newEmails = this.offlineReportEmail.slice();
      const idx = newEmails.indexOf(email);
      newEmails.splice(idx, 1);
      this.offlineReportEmail = newEmails;
    },
    toggleActionModal(show, view) {
      this.showModal = show;
      if (show) {
        this.modalView = view;
      }
    },
    addAlertPreference(value) {
      if (!this.alertPreferenceList.includes(value)) {
        const newList = this.alertPreferenceList.slice();
        newList.push(value);
        this.alertPreferenceList = newList;
      }
    },
    removeAlertPreference(alert) {
      const newList = this.alertPreferenceList.slice();
      const idx = newList.indexOf(alert);
      newList.splice(idx, 1);
      this.alertPreferenceList = newList;
    },
    resetPartnerConfig() {
      const {
        ALERT_EMAIL_PREFERENCE = [],
        spaceTemp = {
          highTemp: 85,
          lowTemp: 54,
        },
        MEMBERS_OFFLINE_REPORT = null,
        MEMBERS_OFFLINE_REPORT_EMAIL = [],
      } = this.partnerSettings;
      this.alertPreferenceList = ALERT_EMAIL_PREFERENCE;
      this.spaceTemp = spaceTemp;
      this.offlineReport = MEMBERS_OFFLINE_REPORT;
      this.offlineReportEmail = MEMBERS_OFFLINE_REPORT_EMAIL;
      this.email = '';
      this.errorObj = {};
    },
    handleChange(field, value) {
      this[field] = value;
      const { spaceTempSetting } = this;
      if (spaceTempSetting.lowPref === 'dynamic') {
        this.errorObj.lowTempErr = null;
      }

      if (spaceTempSetting.highPref === 'dynamic') {
        this.errorObj.highTempErr = null;
      }
    },
    async updatePartnerConfig() {
      const {
        alertPreferenceList,
        spaceTempSetting,
        offlineReport,
        offlineReportEmail,
      } = this;
      const errorObj = {};

      const isHighSpaceTempInvalid = () => alertPreferenceList.includes('High Space Temp')
        && ((spaceTempSetting.highTemp && spaceTempSetting.highTemp < 80)
          || (spaceTempSetting.highTemp && spaceTempSetting.highTemp > 100));

      const isLowSpaceTempInvalid = () => alertPreferenceList.includes('Low Space Temp')
        && ((spaceTempSetting.lowTemp && spaceTempSetting.lowTemp < 40)
          || (spaceTempSetting.lowTemp && spaceTempSetting.lowTemp > 60));

      if (spaceTempSetting?.lowPref === 'custom') {
        if (isLowSpaceTempInvalid() || !spaceTempSetting.lowTemp) {
          errorObj.lowTempErr = 'LST boundary needs to be between 40 and 60';
        }
      }

      if (spaceTempSetting?.highPref === 'custom') {
        if (isHighSpaceTempInvalid() || !spaceTempSetting.highTemp) {
          errorObj.highTempErr = 'HST boundary needs to be between 80 and 100';
        }
      }

      if (offlineReport) {
        if (!offlineReportEmail.length) {
          errorObj.emailErr = 'At least one recipient is required.';
        }
      }
      
      this.errorObj = errorObj;
      if (Object.keys(errorObj).length === 0) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
        this.updating = true;
        if (spaceTempSetting && spaceTempSetting) {
          delete spaceTempSetting.highPref;
          delete spaceTempSetting.lowPref;
        }
        await this.update({
          ALERT_EMAIL_PREFERENCE: alertPreferenceList,
          spaceTemp: spaceTempSetting,
          MEMBERS_OFFLINE_REPORT: offlineReport,
          MEMBERS_OFFLINE_REPORT_EMAIL: offlineReportEmail,
        });
        this.updating = false;
      }
    },
  },
};
</script>
